import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import DatePicker from "react-datepicker";
import { AsyncPaginate } from "react-select-async-paginate";

import {
    fetchAccountFundingOverview,
    fetchFundingRequests,
} from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import SharedTable from "../benefitPlan/SharedTable";
import "./FundingRequests.scss";

const PAGE_SIZE = 40;

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const accountFromSearchParams = (params) => {
    const accountKey = params.get("accountKey");
    if (!accountKey) return null;
    const key = Number(accountKey);
    if (!Number.isFinite(key)) return null;
    const hcc = params.get("accountHccId") || "";
    const name = params.get("accountName") || "";
    const label = name && hcc
        ? `${name} (${hcc} | ${key})`
        : name || hcc || `Account ${key}`;
    return {
        value: key,
        label,
        additional: { accountKey: key, accountHccId: hcc || undefined, accountName: name || undefined },
    };
};

const FundingRequests = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [account, setAccount] = useState(() => accountFromSearchParams(searchParams));
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [search, setSearch] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const [paymentCycleId, setPaymentCycleId] = useState(
        () => searchParams.get("paymentCycleId") || "",
    );

    const { ref, inView } = useInView();
    const asOf = toIsoDate(asOfDate);
    const accountKey = account?.additional?.accountKey;

    useEffect(() => {
        const next = accountFromSearchParams(searchParams);
        if (next?.additional?.accountKey !== account?.additional?.accountKey) {
            setAccount(next);
        }
        const cycle = searchParams.get("paymentCycleId") || "";
        if (cycle !== paymentCycleId) setPaymentCycleId(cycle);
        // Only sync when URL accountKey / cycle changes (cross-links).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("accountKey"), searchParams.get("paymentCycleId")]);

    const onAccountChange = (opt) => {
        setAccount(opt);
        const next = new URLSearchParams(searchParams);
        if (opt?.additional?.accountKey) {
            next.set("accountKey", String(opt.additional.accountKey));
            if (opt.additional.accountHccId) next.set("accountHccId", opt.additional.accountHccId);
            else next.delete("accountHccId");
            if (opt.additional.accountName) next.set("accountName", opt.additional.accountName);
            else next.delete("accountName");
        } else {
            next.delete("accountKey");
            next.delete("accountHccId");
            next.delete("accountName");
        }
        setSearchParams(next, { replace: true });
    };

    const clearPaymentCycleFilter = () => {
        setPaymentCycleId("");
        const next = new URLSearchParams(searchParams);
        next.delete("paymentCycleId");
        setSearchParams(next, { replace: true });
    };

    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error,
        status,
    } = useInfiniteQuery({
        queryKey: [
            "funding-requests",
            accountKey || null,
            asOf,
            searchApplied,
            paymentCycleId || null,
        ],
        queryFn: ({ pageParam = 0 }) =>
            fetchFundingRequests({
                accountKey: accountKey || undefined,
                asOfDate: asOf,
                limit: PAGE_SIZE,
                offset: pageParam,
                q: searchApplied || undefined,
                paymentCycleId: paymentCycleId || undefined,
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((sum, p) => sum + (p?.items?.length || 0), 0);
            const total = lastPage?.total ?? 0;
            if (loaded >= total) return undefined;
            if ((lastPage?.items?.length || 0) < PAGE_SIZE) return undefined;
            return loaded;
        },
        staleTime: 0,
        retry: false,
    });

    const {
        data: overview,
        isLoading: overviewLoading,
        error: overviewError,
    } = useQuery({
        queryKey: ["account-funding-overview", accountKey || null, asOf],
        queryFn: () =>
            fetchAccountFundingOverview({
                accountKey,
                asOfDate: asOf,
            }),
        enabled: !!accountKey,
        staleTime: 30_000,
        retry: false,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const items = useMemo(
        () =>
            (data?.pages || []).flatMap((p) => p?.items || []).map((r) => ({
                ...r,
                fundingRequestFactKey: r.key,
                statusLabel: r.statusLabel || r.status,
                paymentCycleId: r.paymentCycleId || r.cycleKey || null,
            })),
        [data],
    );

    let columns = [
        "fundingRequestFactKey",
        "accountHccId",
        "accountName",
        "statusLabel",
        "paidStatus",
        "paymentCycleId",
        "created",
        "dueDate",
        "claimCount",
        "claimAmount",
        "totalDue",
        "totalPayments",
    ];

    if (showMoreColumns) {
        columns = [
            ...columns,
            "totalCharges",
            "totalAdjustments",
            "adminCharges",
            "externalFees",
            "balanceForward",
        ];
    }

    const clearFilters = () => {
        setAccount(null);
        setAsOfDate(new Date());
        setSearch("");
        setSearchApplied("");
        setPaymentCycleId("");
        setSearchParams({}, { replace: true });
    };

    const onDetailHandler = async (row) => {
        const key = row.fundingRequestFactKey || row.key;
        const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
        navigate(`/finance/funding-requests/${key}${qs}`);
        return {};
    };

    return (
        <div className="container funding-requests">
            <p className="title">Funding Requests</p>
            <p className="sub-title">
                Cash calls to fund claim payables — separate from premium invoices. Created when
                a payment cycle runs (not via a create-FR API). Select an account to see Claims
                Funding float billed vs funding-request due/paid.
            </p>

            {!!paymentCycleId && (
                <p className="funding-requests__cycle-filter">
                    Filtered by payment cycle{" "}
                    <strong>{paymentCycleId}</strong>
                    {" · "}
                    <button
                        type="button"
                        className="funding-requests__link-btn"
                        onClick={clearPaymentCycleFilter}
                    >
                        Clear cycle filter
                    </button>
                </p>
            )}

            <div className="funding-requests__filters">
                <div className="funding-requests__field funding-requests__field--account">
                    <label>Account</label>
                    <AsyncPaginate
                        placeholder="All accounts"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        debounceTimeout={300}
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={onAccountChange}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{
                            ...customStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 200 }),
                        }}
                        isClearable
                    />
                </div>
                <div className="funding-requests__field funding-requests__field--date">
                    <label>As of</label>
                    <DatePicker
                        selected={asOfDate}
                        onChange={(d) => {
                            setAsOfDate(d || new Date());
                        }}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="As of date"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                        popperPlacement="bottom-start"
                        popperClassName="date-picker-popper"
                        popperProps={{ strategy: "fixed" }}
                    />
                </div>
                <div className="funding-requests__field funding-requests__field--search">
                    <label>Search</label>
                    <div className="funding-requests__search-combo">
                        <input
                            type="text"
                            className="funding-requests__search"
                            placeholder="Request #, account HCC, name, status"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") setSearchApplied(search.trim());
                            }}
                        />
                        <button
                            type="button"
                            className="funding-requests__search-btn"
                            onClick={() => setSearchApplied(search.trim())}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {!!accountKey && (
                <section className="funding-requests__overview">
                    <div className="funding-requests__overview-head">
                        <h2>Account funding money</h2>
                        <p>
                            Float = Claims Funding billed on premium invoices (rate × enrollment).
                            Funding request due/paid = cash calls for claim IDs — payment is a
                            separate step, not an automatic bank draft.
                        </p>
                        <p className="funding-requests__overview-links">
                            <Link
                                to={`/finance/payment-cycles?accountKey=${accountKey}${
                                    account?.additional?.accountHccId
                                        ? `&accountHccId=${encodeURIComponent(account.additional.accountHccId)}`
                                        : ""
                                }${
                                    account?.additional?.accountName
                                        ? `&accountName=${encodeURIComponent(account.additional.accountName)}`
                                        : ""
                                }`}
                            >
                                Payment cycles
                            </Link>
                            <Link
                                to={`/finance/payments?accountKey=${accountKey}${
                                    account?.additional?.accountHccId
                                        ? `&accountHccId=${encodeURIComponent(account.additional.accountHccId)}`
                                        : ""
                                }${
                                    account?.additional?.accountName
                                        ? `&accountName=${encodeURIComponent(account.additional.accountName)}`
                                        : ""
                                }`}
                            >
                                Provider payments
                            </Link>
                            <Link
                                to={`/finance/bills?accountKey=${accountKey}${
                                    account?.additional?.accountHccId
                                        ? `&accountHccId=${encodeURIComponent(account.additional.accountHccId)}`
                                        : ""
                                }${
                                    account?.additional?.accountName
                                        ? `&accountName=${encodeURIComponent(account.additional.accountName)}`
                                        : ""
                                }`}
                            >
                                Premium bills
                            </Link>
                        </p>
                    </div>
                    {overviewLoading && (
                        <p className="funding-requests__muted">Loading account overview…</p>
                    )}
                    {overviewError && (
                        <p className="danger">
                            {overviewError.message || "Unable to load funding overview."}
                        </p>
                    )}
                    {overview?.error && (
                        <p className="danger">{overview.error}</p>
                    )}
                    {!overviewLoading && !overviewError && !overview?.error && overview && (
                        <div className="funding-requests__kpis">
                            <div>
                                <span className="funding-requests__kpi-label">
                                    Claims Funding float billed
                                </span>
                                <strong>{money(overview.floatBilled)}</strong>
                            </div>
                            <div>
                                <span className="funding-requests__kpi-label">
                                    Funding adjustments
                                </span>
                                <strong>{money(overview.fundingAdjustments)}</strong>
                            </div>
                            <div>
                                <span className="funding-requests__kpi-label">
                                    FR total due
                                </span>
                                <strong>{money(overview.fundingRequests?.totalDue)}</strong>
                                <span className="funding-requests__kpi-sub">
                                    {overview.fundingRequests?.count ?? 0} requests
                                </span>
                            </div>
                            <div>
                                <span className="funding-requests__kpi-label">
                                    FR payments applied
                                </span>
                                <strong>{money(overview.fundingRequests?.totalPayments)}</strong>
                            </div>
                            <div className="funding-requests__kpi--emphasis">
                                <span className="funding-requests__kpi-label">
                                    FR claim amount
                                </span>
                                <strong>{money(overview.fundingRequests?.claimAmount)}</strong>
                            </div>
                        </div>
                    )}
                </section>
            )}

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0rem 1rem 1rem 0rem",
                }}
            >
                <p style={{ cursor: "pointer", color: "var(--red)" }} onClick={clearFilters}>
                    Clear filters
                </p>
                <p
                    style={{ cursor: "pointer", color: "var(--green)" }}
                    onClick={() => setShowMoreColumns(!showMoreColumns)}
                >
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {status === "error" ? (
                <span className="danger">
                    Error: {error.message || "Failed to load funding requests"}
                </span>
            ) : (
                <SharedTable
                    type="funding-requests"
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    noResultsMessage="No funding requests match the current filters."
                    ref={ref}
                    disableModel
                    onDetailHandler={onDetailHandler}
                />
            )}
        </div>
    );
};

export default FundingRequests;
